/**
 * Helper functions for Structural Extraction Modal
 */

import { Building, Clock, Database, GitBranch, MapPin, User, Wallet } from 'lucide-react'
import type { TabularColumn } from '@/services/api/lake'

// ---------------------------------------------------------------------------
// Entity Icons
// ---------------------------------------------------------------------------

export const ENTITY_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Person: User,
  Company: Building,
  Address: MapPin,
  CryptoAddress: Wallet,
  ExchangeAccount: Wallet,
  Cluster: GitBranch,
  Transaction: Clock,
  Event: Clock,
  Default: Database,
}

export function getEntityIcon(entityType: string) {
  return ENTITY_ICONS[entityType] || ENTITY_ICONS.Default
}

// ---------------------------------------------------------------------------
// Color Palette
// ---------------------------------------------------------------------------

export const COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
]

export function pickColor(index: number): string {
  return COLORS[index % COLORS.length]
}

// ---------------------------------------------------------------------------
// Formatting Helpers
// ---------------------------------------------------------------------------

export function formatRowRange(rows: number[]): string {
  if (rows.length === 0) return '(none)'
  const sorted = [...rows].sort((a, b) => a - b)
  if (sorted.length === 1) return `row ${sorted[0]}`
  const min = sorted[0]
  const max = sorted[sorted.length - 1]
  return `rows ${min}–${max} (${sorted.length} selected)`
}

export function formatColList(cols: number[], dataCols: TabularColumn[]): string {
  if (cols.length === 0) return '(none)'
  const sorted = [...cols].sort((a, b) => a - b)
  return sorted.map(i => dataCols[i]?.name ?? `col${i}`).join(', ')
}

// ---------------------------------------------------------------------------
// Column Sample Extraction
// ---------------------------------------------------------------------------

export function getColumnSamples(
  columnName: string,
  columns: TabularColumn[],
  rows: (string | number | boolean | null)[][],
  maxSamples = 3
): string[] {
  const colIndex = columns.findIndex(c => c.name === columnName)
  if (colIndex === -1) return []

  const samples = new Set<string>()
  for (const row of rows) {
    if (samples.size >= maxSamples) break
    const value = row[colIndex]
    if (value !== null && value !== undefined && value !== '') {
      samples.add(String(value))
    }
  }
  return Array.from(samples)
}
